import type { FC, InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { ErrorMessage } from 'src/components/form/error-message';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import type { FormFieldProps } from 'src/components/form/form-field-props';
import { FormHint } from 'src/components/form/form-hint';
import type { BaseFormOption } from 'src/components/form/form-option';
import { FieldLabel } from 'src/components/form/label';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import { cn } from 'src/utils/cn';

export const RadioLabel: FC<{
  showError?: boolean;
  disabled?: boolean;
  children: ReactNode;
}> = ({ showError, disabled, children }) => {
  return (
    <span
      className={cn(
        `
          cursor-pointer select-none
          legacy:text-legacy-brand
        `,
        showError &&
          `
            text-foreground-support-error
            legacy:text-legacy-error
          `,
        disabled &&
          `
            cursor-not-allowed text-foreground-surface-disabled
            legacy:text-legacy-inactive
          `
      )}
    >
      {children}
    </span>
  );
};

type RadioInputProps = InputHTMLAttributes<HTMLInputElement> & {
  showError: boolean;
};

export const RadioInput = forwardRef<HTMLInputElement, RadioInputProps>(
  (props, ref) => {
    const { disabled, showError, ...rest } = props;

    return (
      <span>
        <input
          type="radio"
          ref={ref}
          {...rest}
          className={cn(
            `
              absolute w-[1px] overflow-hidden p-0
              [clip:rect(0_0_0_0)]
              focus:outline-none
            `,
            rest.className
          )}
          disabled={disabled}
        />
        <div
          className={cn(
            `
              inline-block h-[1.5rem] w-[1.5rem] cursor-pointer rounded-full
              [vertical-align:calc(var(--spacing-icon-offset)*-1)]
              not-legacy:bg-border-surface-default
              legacy:bg-legacy-brand
            `,
            `
              after:block after:h-[1.3rem] after:w-[1.3rem]
              after:translate-[calc((1.5rem-1.3rem)/2)] after:rounded-full
              not-legacy:after:bg-white
              legacy:after:h-[1.4rem] legacy:after:w-[1.4rem]
              legacy:after:translate-[calc((1.5rem-1.4rem)/2)]
              legacy:after:bg-white
            `,
            `
              [:checked~&]:after:block [:checked~&]:after:h-[0.6rem]
              [:checked~&]:after:w-[0.6rem]
              [:checked~&]:after:translate-[calc((1.5rem-0.6rem)/2)]
              [:checked~&]:after:rounded-full
            `,
            `[:focus~&]:legacy-focus-style`,
            showError &&
              `
                not-legacy:bg-border-support-error-subdued
                not-legacy:after:bg-background-support-error-minimal
                legacy:bg-legacy-error legacy:after:bg-legacy-error-lighter
              `,
            disabled &&
              `
                cursor-not-allowed
                not-legacy:bg-border-surface-disabled
                not-legacy:after:border-border-surface-disabled
                not-legacy:after:bg-background-surface-disabled
                legacy:bg-legacy-inactive legacy:after:border-legacy-inactive
                legacy:after:bg-white
              `
          )}
        />
      </span>
    );
  }
);

type SimpleRadioProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  showError: boolean;
};

const SimpleRadio = forwardRef<HTMLInputElement, SimpleRadioProps>(
  (props, ref) => {
    const { showError, label, ...inputProps } = props;
    return (
      <label
        className={`
          inline-grid grid-flow-col items-baseline justify-start gap-xs
          legacy:gap-[0.5rem]
        `}
      >
        {/* empty span needed for firefox */}
        <span>
          <RadioInput ref={ref} showError={showError} {...inputProps} />
        </span>
        <RadioLabel showError={showError} disabled={inputProps.disabled}>
          {label}
        </RadioLabel>
      </label>
    );
  }
);

type RadiosProps = {
  options: BaseFormOption<string>[];
  stacked?: boolean;
  stackedOptions?: boolean;
} & FormFieldProps &
  InputHTMLAttributes<HTMLInputElement>;

/**
 * Use this if you have a short list of options (e.g. 2-5) where the user can only select one.
 *
 * If you have a long list of options, consider using the [Single Select](../?path=/docs/components-form-single-select--docs) component.
 *
 * If you have a long list of options where you want to search, consider using the [Searchable Single Select](../?path=/docs/components-form-searchable-single-select--docs) component.
 */
export const Radios: FC<RadiosProps> = (props) => {
  const { isStacked } = useDefaultStacked();
  const {
    label,
    name,
    options,
    checked,
    stacked = isStacked,
    autoFocus,
    stackedOptions,
    hint,
    hideLabel = false,
    info,
    markAsRequired,
    disabledMessage,
    ...inputProps
  } = props;
  const fieldId = useId();

  const [field, showError, error] = useCustomField(name, label);
  const focusRef = useFocusManager();

  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;

  return (
    <FieldLayout stacked={stacked}>
      {!hideLabel && (
        <FieldLabel
          name={name}
          displayLabel={displayLabel}
          fieldId={fieldId}
          info={info}
          showError={showError}
        />
      )}

      <FieldItem>
        {/* div is needed for layout */}
        <div>
          <div
            className={cn(
              'inline-grid items-start',
              // same as checkbox group
              stackedOptions
                ? `
                  grid-flow-row gap-xs
                  legacy:gap-[0.5rem]
                `
                : `
                  grid-flow-col gap-3xl
                  legacy:gap-[3.5rem]
                `
            )}
            data-testid={field.name}
            data-input-type="radio" // used in unit tests
            data-input-value={field.value} // used in unit tests
          >
            {options.map((option, index) => (
              <Tooltip
                content={
                  inputProps.disabled && disabledMessage
                    ? disabledMessage
                    : undefined
                }
                key={option.value}
              >
                {(targetProps) => (
                  <SimpleRadio
                    // @ts-expect-error
                    ref={focusRef}
                    label={option.label}
                    showError={showError}
                    autoFocus={index === 0 && autoFocus}
                    id={index === 0 ? fieldId : undefined}
                    data-testid={`${field.name}:${option.value}`}
                    {...field}
                    {...inputProps}
                    value={option.value}
                    checked={option.value === field.value}
                    disabled={inputProps.disabled}
                    {...targetProps}
                  />
                )}
              </Tooltip>
            ))}
          </div>
        </div>

        {hint && <FormHint isDisabled={props.disabled}>{hint}</FormHint>}

        {showError && error && (
          <ErrorMessage
            data-testid={`${name}Error`}
            error={error}
            label={label}
          />
        )}
      </FieldItem>
    </FieldLayout>
  );
};
